import {CheckCircle, Shield, TreePine, Award, Heart, Target, Zap } from 'lucide-react';
import { Header, Footer, useQuoteModal, usePageMeta } from '../components/SharedComponents';

const AboutPage = () => {

  const { QuoteModal, openQuoteModal } = useQuoteModal();
  usePageMeta(
    'About Us | One Choice Tree Service — Maryland Tree Care',
    'Learn about One Choice Tree Service: a licensed & insured Maryland tree company serving the Baltimore region with tree removal, trimming, and complete cleanup.'
  );

  // Function to handle quote button clicks
  const handleQuoteClick = () => {
    openQuoteModal();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Header currentPage="about" />

      {/* Hero - Compact & Focused */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">

          {/* Contact Button */}
          <div className="mb-6 flex justify-end">
            <button
              onClick={handleQuoteClick}
              className="inline-flex items-center space-x-2 text-green-600 hover:text-green-700 transition-colors duration-200"
            >
              <span className="font-medium">Get Free Quote</span>
              <span className="text-lg">→</span>
            </button>
          </div>

          <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <CheckCircle className="w-4 h-4 mr-2" />
            Maryland Tree Service - Simply Done Right
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            The Project is Done.<br />
            <span className="text-green-600">Your Yard Looks Perfect.</span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            That's our goal. That's what we do.
          </p>
        </div>
      </section>

      {/* Who We Are - Split Design */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            
            {/* Content - 3 columns */}
            <div className="lg:col-span-3">
              <div className="mb-6">
                <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
                  WHO WE ARE
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  How We Think About Trees
                </h2>
              </div>

              <div className="prose prose-lg text-gray-600 mb-6">
                <p>
                  Most people call us when they have a problem. A tree too close to the house. Storm damage. Something that just doesn't look right anymore. We show up, we listen, we solve it. Then we leave your property exactly how you'd want to find it.
                </p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="text-2xl font-bold text-gray-900">2025</div>
                  <div className="text-sm text-gray-600">Year Founded</div>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="text-2xl font-bold text-gray-900">Every</div>
                  <div className="text-sm text-gray-600">Job Done Clean</div>
                </div>
              </div>
            </div>

            {/* Visual - 2 columns */}
            <div className="lg:col-span-2">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden h-80">
                  <img 
                    src="https://images.pexels.com/photos/31127615/pexels-photo-31127615/free-photo-of-arborist-cutting-down-tree-trunk-safely-with-chainsaw.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
                    alt="Professional tree service team working"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-gray-700">FREE ESTIMATES 24/7</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission - Card Grid Style */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          
          {/* Header */}
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              OUR MISSION
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              What Matters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We remove trees. We clean up completely. We don't damage anything else. Simple.
            </p>
          </div>

          {/* Mission Cards - Staggered Layout */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Card 1 - Larger */}
            <div className="md:col-span-2 lg:col-span-1 bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Licensed. Insured.</h3>
              <p className="text-gray-600 leading-relaxed">
                Because that's how you do this work properly. Full stop.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-8 text-white shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <TreePine className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Clean. Always.</h3>
              <p className="text-green-100 leading-relaxed">
                We take only what needs to go. We leave your yard better than we found it. Every time.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">One Price.</h3>
              <p className="text-gray-600 leading-relaxed">
                We tell you what it costs. That's what you pay. Nothing hidden. Nothing extra.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Roots - Timeline Style */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              OUR ROOTS
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Why We Started This Business
            </h2>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Central line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-green-200"></div>
            
            <div className="space-y-12">
              
              {/* Timeline Item 1 */}
              <div className="flex items-center">
                <div className="flex-1 text-right pr-8">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Founded</h3>
                    <p className="text-3xl font-bold text-green-600 mb-2">2025</p>
                    <p className="text-gray-600">New company. Old principles. Do the work right.</p>
                  </div>
                </div>
                <div className="w-4 h-4 bg-green-600 rounded-full relative z-10"></div>
                <div className="flex-1 pl-8"></div>
              </div>

              {/* Timeline Item 2 */}
              <div className="flex items-center">
                <div className="flex-1 pr-8"></div>
                <div className="w-4 h-4 bg-green-600 rounded-full relative z-10"></div>
                <div className="flex-1 text-left pl-8">
                  <div className="bg-green-50 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Headquarters</h3>
                    <p className="text-xl font-bold text-green-600 mb-2">Maryland</p>
                    <p className="text-gray-600">We know these trees. We know this weather. We know what works.</p>
                  </div>
                </div>
              </div>

              {/* Timeline Item 3 */}
              <div className="flex items-center">
                <div className="flex-1 text-right pr-8">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Service Area</h3>
                    <p className="text-xl font-bold text-green-600 mb-2">Anne Arundel, Howard, Baltimore & Carroll Counties</p>
                    <p className="text-gray-600">Close enough to come quickly. Experienced enough to handle anything.</p>
                  </div>
                </div>
                <div className="w-4 h-4 bg-green-600 rounded-full relative z-10"></div>
                <div className="flex-1 pl-8"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why One Choice - Feature Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Content */}
            <div>
              <span className="inline-block bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold mb-4">
                WHY ONE CHOICE?
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                One Choice
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Because when something is done right, you don't need to think about it again.
              </p>

              {/* Benefits List */}
              <div className="space-y-4">
                {[
                  { icon: Target, text: "The tree is gone" },
                  { icon: Shield, text: "Nothing else is damaged" },
                  { icon: Heart, text: "Your yard looks perfect" }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-300">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-3xl p-8 text-center">
                <div className="bg-white/10 rounded-2xl p-8 mb-6">
                  <Zap className="w-16 h-16 mx-auto mb-4 text-white" />
                  <h3 className="text-2xl font-bold text-white mb-2">Simply Done</h3>
                  <p className="text-green-100">Tree removal. Complete cleanup. Perfect results.</p>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-white">100%</div>
                    <div className="text-xs text-green-100">Licensed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">100%</div>
                    <div className="text-xs text-green-100">Insured</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">100%</div>
                    <div className="text-xs text-green-100">Clean</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Different Style */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-3xl p-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Free Estimate Upon Request
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Submit a request below. We'll tell you what it costs. We'll get it done.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleQuoteClick}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                Get a Free Quote
              </button>
              <a
                href="tel:+14432080747"
                className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all"
              >
                Call (443) 208-0747
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      {/* Add the QuoteModal component */}
      <QuoteModal />
      
    </div>
  );
};

export default AboutPage;